import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import _ from 'lodash';

import { Model } from '../common/model';
import { DeviceModel } from '../models/device.model';
import { LocationModel } from '../models/location.model';
import { SmartenitTranslationService } from '../common/translation/smartenit-translation.service';
import { TemperatureConverterService } from '../common/temperature-converter.service';
import { IWebSocketDeviceMessage } from '../websockets/websocket-device-message.interface';
import { ISmartenitPluginMessage } from './smartenit-plugin-message.interface';
import { DatabaseService } from '../storage/database.service';
import { DevicesService } from '../resources/devices.service';
import { ActionsService } from '../resources/actions.service';
import { ConditionsService } from '../resources/conditions.service';
import { EffectsService } from '../resources/effects.service';
import { MetricsService } from '../resources/metrics.service';
import { PluginUtilsService } from './plugin-utils.service';

export abstract class SmartenitPlugin {
  private _name: string;
  private _resource: Model;
  private _componentId: string;
  private _processorName: string;
  private _state: any;
  private _stateTTL: number;
  private _customOptions: any;

  get stateTTL(): number {
    return this._stateTTL;
  }

  set stateTTL(value: number) {
    this._stateTTL = value;
  }

  get name(): string {
    return this._name;
  }

  get device(): DeviceModel {
    return this._resource as DeviceModel;
  }

  get location(): LocationModel {
    return this._resource as LocationModel;
  }

  get componentId(): string {
    return this._componentId;
  }

  get processorName(): string {
    return this._processorName;
  }

  get customOptions(): any {
    return this._customOptions;
  }

  set customOptions(value: any) {
    this._customOptions = value;
    this.onCustomOptionsChanged();
  }

  protected onCustomOptionsChanged() {}

  get state(): any {
    return this._state;
  }

  set state(value: any) {
    this._state = value;
  }

  public saveState(state: any, ttl?: number, timestamp?: number) {
    this._state = state;

    if (ttl === undefined) {
      ttl = this.stateTTL;
    }

    this.setCache('state', this._state, ttl, undefined, timestamp);
  }

  private _onData: Subject<ISmartenitPluginMessage>;

  get onData(): Observable<ISmartenitPluginMessage> {
    return this._onData.asObservable();
  }

  protected _onUpdate: Subject<any>;

  get onUpdate(): Observable<any> {
    return this._onUpdate.asObservable();
  }

  private resourceIsDevice(): boolean {
    return this._resource instanceof DeviceModel;
  }

  private resourceIsLocation(): boolean {
    return this._resource instanceof LocationModel;
  }

  public getStatus(
    context?: string,
    force?: boolean,
    subscribe?: boolean,
    payload?: any,
    sendDirect: boolean = false
  ): void {
    if (payload && Object.keys(payload).length > 0) {
      payload = Object.assign(this.getStatusPayload(), payload);
    } else {
      payload = this.getStatusPayload();
    }

    if (this.resourceIsDevice()) {
      const query = `cacheTTL=${this.stateTTL}`;
      if (force === true) {
        this.device.getStatus(
          this._componentId,
          this._processorName,
          payload,
          query,
          sendDirect
        );
      } else {
        this.getCachedValues(true).subscribe((cachedState: any) => {
          if (cachedState == null || cachedState == undefined) {
            this.device.getStatus(
              this._componentId,
              this._processorName,
              payload,
              query,
              sendDirect
            );
          }
        });
      }
    }
  }

  public getStatusPayload(): any {
    return {};
  }

  public getReportableAttrs(): any {
    return [];
  }

  public getCachedValues(subscribe?: boolean): any {
    const observable = this.getCache().map((cachedState: any) => {
      if (cachedState != null) {
        this.state = cachedState;

        this.device.cached = true;

        this._onUpdate.next({
          state: this.state
        });
      }

      return cachedState;
    });

    if (subscribe === true) {
      return observable;
    }

    const subscription: Subscription = observable.subscribe(() =>
      subscription.unsubscribe()
    );
  }

  private parseCachePath(path: string): any {
    if (path) {
      const parts = path.split('-');
      return {
        attributeOrMethod: parts[3],
        componentId: parts[0],
        processorName: parts[1]
      };
    }

    return null;
  }

  constructor(
    name: string,
    componentId: string,
    processorName: string,
    resource: Model,
    public dbService: DatabaseService,
    public devicesService: DevicesService,
    public actionsService: ActionsService,
    public conditionsService: ConditionsService,
    public effectsService: EffectsService,
    public pluginUtilsService: PluginUtilsService,
    public metricsService: MetricsService,
    public translationService: SmartenitTranslationService,
    public temperatureConverterService: TemperatureConverterService
  ) {
    this._name = name;
    this._resource = resource;
    this._componentId = componentId;
    this._processorName = processorName;
    this._onUpdate = new Subject<any>();
    this._state = {};
    this._stateTTL = 10 * 60;

    if (this.resourceIsDevice()) {
      this.device.onDeviceData.subscribe((message: IWebSocketDeviceMessage) => {
        let timestamp;
        if (_.get(message, 'data.apiCache')) {
          timestamp = _.get(message, 'data.response.timestamp', undefined);
        }

        this.processDeviceMessage(message, timestamp);

        if (
          message &&
          message.componentId === this._componentId &&
          message.processorName === this._processorName
        ) {
          this.saveState(
            this.processMessage(message, timestamp),
            undefined,
            timestamp
          );
        }
      });

      // handle api cache
      this.device.onDeviceResponse.subscribe((response: any) => {
        if (response && response.response) {
          const {
            attributeOrMethod,
            componentId: componentIdRes,
            processorName: processorNameRes
          } = this.parseCachePath(response.key);

          if (
            'cache' in response.response &&
            'timestamp' in response.response &&
            'value' in response.response
          ) {
            // LAPI Cache
            response.response = {
              apiCache: response.response.cache,
              data: [
                {
                  attr: attributeOrMethod,
                  v: {
                    timestamp: response.response.timestamp,
                    value: response.response.value
                  }
                }
              ]
            };
          }

          if (response.response.apiCache == true) {
            if (
              componentIdRes != undefined &&
              processorNameRes != undefined &&
              attributeOrMethod != undefined
            ) {
              if (response.response.data) {
                // sort cache based on timestamp for consistency
                const responseData = response.response.data;

                responseData.sort((a: any, b: any) => {
                  return a && a.v ? a.v.timestamp - b.v.timestamp : 0;
                });

                for (let i = 0; i < responseData.length; i++) {
                  const singleResponse = responseData[i];

                  if (
                    singleResponse &&
                    singleResponse.v &&
                    singleResponse.attr
                  ) {
                    const message: IWebSocketDeviceMessage = {
                      attributeOrMethod: singleResponse.attr,
                      componentId: componentIdRes,
                      data: { response: singleResponse.v, apiCache: true },
                      processorName: processorNameRes,
                      resource: 'devices',
                      resourceId: this.device._id
                    };

                    // trigger local message to websocket to follow same process
                    this.device.webSocketsService.proxyMessage(message);
                  }
                }
              }
            }
          }
        }
      });

      // Get cached state
      this.getCachedValues(true).subscribe(
        (cachedState: any) => {
          if (cachedState !== undefined && cachedState != null) {
            this._state = cachedState;
          }

          this.onInit();
        },
        () => {
          this.onInit();
        }
      );
    } else {
      this.onInit();
    }
  }

  protected abstract processMessage(
    data: IWebSocketDeviceMessage,
    timestamp?: number
  ): any;

  protected processDeviceMessage(
    data: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {}

  public getRawValue(value: any, attribute?: string, method?: string): any {
    return value;
  }

  public getProcessedValue(
    value: any,
    attribute?: string,
    method?: string
  ): any {
    return value;
  }

  /**
   * Executed when the plugin has loaded cache information if available
   */
  public onInit() {}

  public getCacheKey(category: string = ''): string {
    if (this.resourceIsDevice()) {
      return [
        this.device._id,
        this._componentId,
        this._processorName,
        category
      ].join('_');
    } else if (this.resourceIsLocation()) {
      return [
        this.location._id,
        this._componentId,
        this._processorName,
        category
      ].join('_');
    }

    return '';
  }

  public setCache(
    category: string,
    data: any,
    ttl?: number,
    subscribe?: boolean,
    timestamp?: number
  ): any {
    const observable = this.dbService
      .getCollection('devices_cache')
      .save(this.getCacheKey(category), data, ttl, timestamp);

    if (subscribe === true) {
      return observable;
    }

    return observable.subscribe(() => {});
  }

  public getCache(category: string = 'state'): Observable<any> {
    return this.dbService
      .getCollection('devices_cache')
      .getById(this.getCacheKey(category));
  }

  public getReadAttributeDelay() {
    const readAttributeDelay = _.get(this.customOptions, 'readRequestsDelay');
    return readAttributeDelay != null && !isNaN(Number(readAttributeDelay))
      ? Number(readAttributeDelay)
      : undefined;
  }

  public executeMethod(
    componentId: string,
    processorName: string,
    method: string,
    payload: any,
    requestStatus?: boolean,
    payloadStatus?: any,
    query?: string
  ) {
    if (this.resourceIsDevice()) {
      if (requestStatus == undefined) {
        requestStatus = true;
      }

      if (requestStatus && payloadStatus == undefined) {
        payloadStatus = this.getStatusPayload();
      }

      this.device.executeMethod(
        componentId,
        processorName,
        method,
        payload,
        requestStatus,
        payloadStatus,
        query,
        undefined,
        this.getReadAttributeDelay()
      );
    }
  }

  public setAttributeWithSubscribeOption(
    componentId: string,
    processorName: string,
    attribute: string,
    payload: any,
    subscribe: boolean,
    readAttribute?: boolean
  ) {
    if (this.resourceIsDevice()) {
      const observable = this.device.setAttribute(
        componentId,
        processorName,
        attribute,
        payload,
        readAttribute,
        this.getReadAttributeDelay()
      );

      if (subscribe === true) {
        return observable;
      }

      const subscription: Subscription = observable.subscribe(() =>
        subscription.unsubscribe()
      );
    }
  }

  public getAttributeWithSubscribeOption(
    componentId: string,
    processorName: string,
    attribute: string,
    payload: any,
    subscribe: boolean,
    query?: string
  ) {
    if (this.resourceIsDevice()) {
      const observable = this.device.getAttribute(
        componentId,
        processorName,
        attribute,
        payload,
        query
      );

      if (subscribe === true) {
        return observable;
      }

      const subscription: Subscription = observable.subscribe(() =>
        subscription.unsubscribe()
      );
    }
  }

  public getValueDescription(value: any): string {
    return '';
  }
}
